import { useRef } from "react";
import { createPortal } from "react-dom";
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ClipboardDocumentCheckIcon,
  ForwardIcon,
} from "@heroicons/react/24/solid";
import useClickOutside from "../../../hooks/useClickOutside";
import InterviewScorecard from "./InterviewScorecard";

type Props = {
  candidateId: string;
  roleId: string;
  roleStatusId: string;
  stageName?: string;
  candidateName?: string;
  /**
   * "Excellent" or "Pass" - whichever grade click triggered this modal. Present for
   * both grade-click cases (required and offered-with-skip); absent when opened
   * voluntarily via the "Scorecard" button, where there's no pending grade to report on.
   */
  attemptedRating?: string;
  onClose: () => void;
  /**
   * True only for the very first grade given on a final-interview stage (see
   * ScoreGrades' handleGradeClick) - the scorecard is offered rather than required
   * there, with a way to bypass it entirely. Every grade after the first acts directly
   * with no modal at all, so this never appears on a stage that already has a grade.
   */
  allowSkip?: boolean;
  /** Records the originally-clicked grade directly, bypassing the scorecard. Only
   *  ever called when allowSkip is true. */
  onSkip?: () => void;
  /** Fired right after the embedded scorecard is submitted, so the page behind this
   *  modal (the grade box, the structured-evaluation badge) can refresh live. */
  onSubmitted?: () => void;
};

type Mode = "required" | "offered" | "voluntary";

/**
 * Three ways to land here:
 *  - Required (amber): a Pass/Excellent click was rejected because this non-final
 *    Interview stage has never had a structured evaluation submitted.
 *  - Offered (blue, allowSkip): the very first Pass/Excellent grade on a
 *    final-interview stage - shown once, with a way to skip it and grade directly.
 *  - Voluntary (neutral): opened via the "Scorecard" button, independent of any grade
 *    click - no attemptedRating, no skip needed since nothing is pending.
 *
 * Any of these renders InterviewScorecard, which only needs
 * candidateId/roleId/roleStatusId, not a slot id, so it works here without navigating
 * away from the candidate page.
 */
export default function RequiredScorecardModal({
  candidateId,
  roleId,
  roleStatusId,
  stageName,
  candidateName,
  attemptedRating,
  onClose,
  allowSkip,
  onSkip,
  onSubmitted,
}: Props) {
  const modalRef = useRef(null);
  useClickOutside(modalRef, onClose);

  const mode: Mode = allowSkip ? "offered" : attemptedRating ? "required" : "voluntary";

  const theme = {
    required: {
      banner: "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800",
      icon: "text-amber-600 dark:text-amber-400",
      title: "text-amber-900 dark:text-amber-200",
      body: "text-amber-800 dark:text-amber-300",
      close: "text-amber-600 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40",
    },
    offered: {
      banner: "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800",
      icon: "text-blue-600 dark:text-blue-400",
      title: "text-blue-900 dark:text-blue-200",
      body: "text-blue-800 dark:text-blue-300",
      close: "text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40",
    },
    voluntary: {
      banner: "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700",
      icon: "text-entntblue",
      title: "text-gray-900 dark:text-gray-100",
      body: "text-gray-500 dark:text-gray-400",
      close: "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700",
    },
  }[mode];

  const modalContent = (
    <div className="fixed inset-0 flex justify-center z-50 h-screen backdrop-blur-sm">
      <div
        className="relative p-2 shadow-lg md:w-[80%] w-[95%] max-h-[90%] overflow-y-auto dark:bg-gray-800 bg-gray-50 top-14 rounded-md border border-gray-300 dark:border-gray-600"
        ref={modalRef}
      >
        <div className="mx-auto px-4 pt-0">
          <div className={`${theme.banner} border rounded-lg mb-4 flex items-start justify-between gap-3 p-4`}>
            <div className="flex items-start gap-3">
              {mode === "required" && (
                <ExclamationTriangleIcon className={`h-5 w-5 mt-0.5 shrink-0 ${theme.icon}`} />
              )}
              {mode === "offered" && (
                <InformationCircleIcon className={`h-5 w-5 mt-0.5 shrink-0 ${theme.icon}`} />
              )}
              {mode === "voluntary" && (
                <ClipboardDocumentCheckIcon className={`h-5 w-5 mt-0.5 shrink-0 ${theme.icon}`} />
              )}
              <div>
                <h2 className={`text-base font-semibold ${theme.title}`}>
                  {mode === "required" && "Structured evaluation required"}
                  {mode === "offered" && "Scorecard available for this stage"}
                  {mode === "voluntary" && "Structured evaluation"}
                </h2>
                <p className={`text-sm mt-1 ${theme.body}`}>
                  {mode === "required" && (
                    <>
                      Please fill in and submit this scorecard for{" "}
                      <strong>{candidateName ?? "this candidate"}</strong>
                      {stageName ? ` at the ${stageName} stage` : ""} before recording a{" "}
                      <strong>{attemptedRating}</strong> grade. 
                    </>
                  )}
                  {mode === "offered" && (
                    <>
                      This is a final interview stage, so filling in a scorecard for{" "}
                      <strong>{candidateName ?? "this candidate"}</strong> is optional. Fill
                      it in now if you&rsquo;d like a structured record, or skip it and
                      record the <strong>{attemptedRating}</strong> grade directly.
                    </>
                  )}
                  {mode === "voluntary" && (
                    <>
                      {candidateName ?? "This candidate"}
                      {stageName ? ` · ${stageName}` : ""}. Filling this in is optional -
                      the grade buttons on the candidate page work independently of it.
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {mode === "offered" && (
                <button
                  type="button"
                  onClick={onSkip}
                  className="inline-flex items-center gap-1.5 rounded-md bg-entntblue px-3.5 py-2 text-xs font-bold text-white shadow-sm transition-colors hover:bg-blue-700"
                >
                  <ForwardIcon className="h-4 w-4" />
                  Skip &amp; grade directly
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className={`p-2 rounded-lg transition-colors ${theme.close}`}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
            <InterviewScorecard
              candidateId={candidateId}
              roleId={roleId}
              roleStatusId={roleStatusId}
              stageName={stageName}
              candidateName={candidateName}
              onSubmitted={onSubmitted}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.getElementById("root") as HTMLElement);
}
